import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    NgZone,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { NgbTooltip, NgbTooltipModule, PlacementArray } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'sas-nav-item',
    imports: [NgbTooltipModule, TranslatePipe],
    templateUrl: './sas-nav-item.component.html',
    styleUrl: './sas-nav-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class SasNavItemComponent {
    private readonly router = inject(Router);
    private readonly elementRef = inject(ElementRef<HTMLElement>);
    private readonly translate = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly ngZone = inject(NgZone);
    readonly active           = input<boolean>(false);
    readonly collapsed        = input<boolean>(false);
    readonly label            = input<string>('');
    /** Internal navigation — passed to router.navigate() on click. */
    readonly navRoute         = input<string[] | string | null>(null);
    /** External URL — renders as native href with target="_blank". */
    readonly href             = input<string | null>(null);
    readonly tabIndex         = input<number>(0);
    /** Extra CSS class forwarded to the <a> (e.g. option code for test selectors). */
    readonly itemClass        = input<string | null>(null);
    readonly tooltipPlacement = input<PlacementArray>(['end', 'top', 'bottom']);

    readonly tooltipDirective = viewChild(NgbTooltip);

    protected readonly isCropped = signal<boolean>(false);
    protected readonly tooltipContent = signal<string>('');

    readonly resolvedTooltipText = computed(() => {
        return this.tooltipContent() || (this.label() ? this.translate.instant(this.label()) : '');
    });

    readonly isTooltipVisible = computed(() => {
        return (this.collapsed() || this.isCropped()) && Boolean(this.resolvedTooltipText());
    });

    constructor() {
        // Close tooltip and reset crop state when collapsed state changes
        effect((onCleanup) => {
            this.collapsed();
            this.tooltipDirective()?.close();
            this.isCropped.set(false);

            // Re-evaluate crop after expand/collapse settles if mouse is still hovering
            const timer = setTimeout(() => {
                if (this.elementRef.nativeElement.matches(':hover') || this.elementRef.nativeElement.matches(':focus-within')) {
                    this.updateTooltipState();
                }
            }, 350);
            onCleanup(() => clearTimeout(timer));
        });

        // Close tooltip when window or any ancestor container scrolls.
        // Outside the zone: a capture listener per item would otherwise run change detection on every scroll event.
        if (typeof window !== 'undefined') {
            const onScroll = () => {
                if (this.tooltipDirective()?.isOpen()) {
                    this.ngZone.run(() => this.tooltipDirective()?.close());
                }
            };
            this.ngZone.runOutsideAngular(() => {
                window.addEventListener('scroll', onScroll, { capture: true, passive: true });
            });
            this.destroyRef.onDestroy(() => {
                window.removeEventListener('scroll', onScroll, { capture: true });
            });
        }
    }

    onMouseEnter(): void {
        this.updateTooltipState();
    }

    onFocusIn(): void {
        this.updateTooltipState();

        // Mouse clicks also focus the link; only keyboard focus (:focus-visible) opens the tooltip.
        // Deferred one tick so [disableTooltip] reflects the state computed above.
        const link = this.elementRef.nativeElement.querySelector('.nav-link') as HTMLElement | null;
        setTimeout(() => {
            if (link?.matches(':focus-visible')) {
                this.tooltipDirective()?.open();
            }
        });
    }

    onFocusOut(): void {
        this.tooltipDirective()?.close();
    }

    private isSidebarTransitioning(): boolean {
        if (typeof document === 'undefined') {return false;}
        const sidebar = this.elementRef.nativeElement.closest('.sidebar') as HTMLElement | null;
        const app = document.querySelector('#app') as HTMLElement | null;
        const sidebarAnims = typeof sidebar?.getAnimations === 'function' ? sidebar.getAnimations() : [];
        const appAnims = typeof app?.getAnimations === 'function' ? app.getAnimations() : [];
        return sidebarAnims.length > 0 || appAnims.length > 0;
    }

    private updateTooltipState(): void {
        const labelEl = this.elementRef.nativeElement.querySelector('.sas-nav-item__label') as HTMLElement | null;
        const renderedText = labelEl?.textContent?.trim();
        const fallbackText = this.label() ? this.translate.instant(this.label()) : '';
        const fullText = renderedText || fallbackText;
        this.tooltipContent.set(fullText);

        if (this.collapsed()) {
            this.isCropped.set(false);
            return;
        }

        if (this.isSidebarTransitioning()) {
            this.isCropped.set(false);
            const sidebar = this.elementRef.nativeElement.closest('.sidebar') as HTMLElement | null;
            const app = document.querySelector('#app') as HTMLElement | null;
            const anims = [
                ...(typeof sidebar?.getAnimations === 'function' ? sidebar.getAnimations() : []),
                ...(typeof app?.getAnimations === 'function' ? app.getAnimations() : []),
            ];
            Promise.all(anims.map(a => a.finished.catch(() => {}))).then(() => {
                if (this.elementRef.nativeElement.matches(':hover') || this.elementRef.nativeElement.matches(':focus-within')) {
                    this.measureCrop(labelEl);
                }
            });
            return;
        }

        this.measureCrop(labelEl);
    }

    private measureCrop(labelEl: HTMLElement | null): void {
        if (this.collapsed()) {
            this.isCropped.set(false);
            return;
        }

        if (labelEl) {
            // Text is cropped if scrollWidth exceeds clientWidth (+1px subpixel tolerance)
            const cropped = labelEl.scrollWidth > labelEl.clientWidth + 1;
            this.isCropped.set(cropped);
            if (!cropped && this.tooltipDirective()?.isOpen()) {
                this.tooltipDirective()?.close();
            }
        } else {
            this.isCropped.set(false);
        }
    }

    /**
     * Resolves the final href attribute value:
     * - External link  → href() directly (e.g. "https://external.com").
     * - Internal route → serialised via router.serializeUrl() so the anchor
     *   has a proper href for right-click / open-in-new-tab, without any
     *   RouterLink @HostBinding conflict.
     */
    readonly resolvedHref = computed(() => {
        const ext = this.href();
        if (ext != null) {return ext;}

        const route = this.navRoute();
        if (route == null) {return null;}

        const commands = Array.isArray(route) ? route : [route];
        try {
            return this.router.serializeUrl(this.router.createUrlTree(commands));
        } catch {
            return null;
        }
    });

    /**
     * Click handler:
     * - External links: no-op — browser follows the href naturally.
     * - Internal routes: prevents page reload, delegates to Angular Router
     *   (same guard pipeline as RouterLink).
     */
    onLinkClick(event: MouseEvent): void {
        this.tooltipDirective()?.close();

        if (this.href()) {return;}

        const route = this.navRoute();
        if (route == null) {return;}

        event.preventDefault();
        const commands = Array.isArray(route) ? route : [route];
        this.router.navigate(commands);
    }
}
